export interface IRequestedAmount {
  currencyDescription?: string;
  currencyIsoCode?: string;
  currencySymbol?: string;
  currencyId?: string;
  totalAmount?: number;
  denominations?: ISolCustomerDenominationQuantity[];
  packs?: ISolCustomerPackQuantity[];
}

export enum SolCustomerOrderProductCode {
  PICKUP_CASH = "1",
  PICKUP_OTHERS = "2",
  DELIVERY_CASH = "11",
  DELIVERY_OTHERS = "12",
  DELIVERY_EXCHANGE = "55",
  ASSISTANCE = "40",
  SUPPLY_MATERIAL = "14",
}

export enum SolCustomerOrderServiceType {
  PICKUP = 0,
  DELIVERY = 1,
  ASSISTANCE = 3,
  SUPPLY_MATERIAL = 4,
}

export enum SolCustomerOrderType {
  PICKUP = "PICKUP",
  DELIVERY = "DELIVERY",
  DELIVERY_EXCHANGE = "DELIVERY_EXCHANGE",
  ASSISTANCE = "ASSISTANCE",
  SUPPLY_MATERIAL = "SUPPLY_MATERIAL",
}

const ORDER_TYPE_BY_PRODUCT_CODE: Record<string, SolCustomerOrderType> = {
  [SolCustomerOrderProductCode.PICKUP_CASH]: SolCustomerOrderType.PICKUP,
  [SolCustomerOrderProductCode.PICKUP_OTHERS]: SolCustomerOrderType.PICKUP,
  [SolCustomerOrderProductCode.DELIVERY_CASH]: SolCustomerOrderType.DELIVERY,
  [SolCustomerOrderProductCode.DELIVERY_OTHERS]: SolCustomerOrderType.DELIVERY,
  [SolCustomerOrderProductCode.DELIVERY_EXCHANGE]: SolCustomerOrderType.DELIVERY,
  [SolCustomerOrderProductCode.ASSISTANCE]: SolCustomerOrderType.ASSISTANCE,
  [SolCustomerOrderProductCode.SUPPLY_MATERIAL]: SolCustomerOrderType.SUPPLY_MATERIAL,
};

const ORDER_TYPE_BY_SERVICE_TYPE: Partial<
  Record<SolCustomerOrderServiceType, SolCustomerOrderType>
> = {
  [SolCustomerOrderServiceType.PICKUP]: SolCustomerOrderType.PICKUP,
  [SolCustomerOrderServiceType.DELIVERY]: SolCustomerOrderType.DELIVERY,
  [SolCustomerOrderServiceType.ASSISTANCE]: SolCustomerOrderType.ASSISTANCE,
  [SolCustomerOrderServiceType.SUPPLY_MATERIAL]: SolCustomerOrderType.SUPPLY_MATERIAL,
};

const ORDER_DESCRIPTION_KEY_BY_TYPE: Record<SolCustomerOrderType, string> = {
  [SolCustomerOrderType.PICKUP]: "label.pickup",
  [SolCustomerOrderType.DELIVERY]: "label.delivery",
  [SolCustomerOrderType.DELIVERY_EXCHANGE]: "label.deliveryExchange",
  [SolCustomerOrderType.ASSISTANCE]: "label.technicalAssistance",
  [SolCustomerOrderType.SUPPLY_MATERIAL]: "label.supplyMaterialService",
};

export const resolveSolCustomerOrderType = (
  productCode?: string,
  serviceType?: number
): SolCustomerOrderType | undefined => {
  if (productCode && ORDER_TYPE_BY_PRODUCT_CODE[productCode]) {
    return ORDER_TYPE_BY_PRODUCT_CODE[productCode];
  }

  if (typeof serviceType === "number") {
    return ORDER_TYPE_BY_SERVICE_TYPE[serviceType as SolCustomerOrderServiceType];
  }

  return undefined;
};

export const resolveSolCustomerOrderDescriptionKey = (
  orderType?: SolCustomerOrderType,
  isChange?: boolean
): string | undefined => {
  if (!orderType) {
    return undefined;
  }

  if (orderType === SolCustomerOrderType.DELIVERY && isChange) {
    return "label.deliveryExchange";
  }

  return ORDER_DESCRIPTION_KEY_BY_TYPE[orderType];
};

export interface IOrderTypeOption {
  type: SolCustomerOrderType;
  labelKey: string;
}

export const getLimitOrderTypeOptions = (
  labelOverrides?: Partial<Record<SolCustomerOrderType, string>>
): IOrderTypeOption[] => {
  const typesToInclude = [SolCustomerOrderType.PICKUP, SolCustomerOrderType.DELIVERY];
  return typesToInclude.map((type) => ({
    type,
    labelKey: labelOverrides?.[type] ?? ORDER_DESCRIPTION_KEY_BY_TYPE[type],
  }));
};

export interface IRequestHistorialsService {
  isChange?: boolean;
  origin?: string;
  requestFinalTime?: string;
  requestInitialTime?: string;
  plannedFinalTime?: string;
  plannedInitialTime?: string;
  realFinalTime?: string;
  realInitialTime?: string;
  createdAt?: string;
  approvedAt?: string;
  comments?: string;
  requestedAmounts?: IRequestedAmount[];
  requestedTime?: string;
  serviceStatusCode?: string;
  serviceStatusDescription?: string;
  serviceType?: number;
  productCode?: string;
  productDescription?: string;
  serviceId?: string;
  serviceCode?: string;
  serviceRate?: number;
  serviceRateId?: string;
  consignmentStatusCode?: string;
}

export interface IRequestHistorials {
  services?: IRequestHistorialsService[];
}

export interface ISolCustomerOrderServiceContainer {}

export interface IPickUpService extends ISolCustomerOrderService {
  isChange?: boolean;
  requestedAmounts?: IRequestedAmount[];
  serviceAmount?: string;
  agreementCommodities?: ISolCustomerCommodity[];
}

export interface IDeliveryService extends ISolCustomerOrderService {
  isChange?: boolean;
  requestedAmounts?: IRequestedAmount[];
}

export interface IFault {
  faultCode: string;
  faultDescription?: string;
  faultID: string;
}

export interface IAssistanceService extends ISolCustomerOrderService {
  assistanceObservation?: string;
  faultDescription?: string;
}

export interface ISolCustomerDenominationPack {
  code?: string;
  currencyIsoCode?: string;
  currencySymbol?: string;
  description?: string;
  id?: string;
  quantity?: number;
  value?: number;
}

export interface ISolCustomerPack {
  code?: string;
  denominations?: ISolCustomerDenominationPack[];
  description?: string;
  id?: string;
}

export interface ISolCustomerDenominationQuantity {
  denominationQuantity?: number | null;
  denominationId: string;
}
export interface ISolCustomerPackQuantity {
  packQuantity?: number | null;
  packId: string;
}

export interface ISolCustomerCommodity {
  code?: string;
  description?: string;
  id?: string;
}

export interface ISolCustomerCommodityType {
  code?: string;
  commodities?: ISolCustomerCommodity[];
  description?: string;
  id?: string;
}

export interface ISolCustomerSupply {
  commodityTypeId?: string;
  commodityTypeDescription?: string;
  commodityId?: string;
  commodityDescription?: string;
  quantity?: number;
}

export interface ISolCustomerSupplyMaterialService extends ISolCustomerOrderService {
  supplies?: ISolCustomerSupply[];
}

export interface ISolCustomerServiceRating {
  countryCode?: string;
  serviceId: string;
  rate: number;
  comments: string;
  optionsIds?: any[];
}

export interface ISolCustomerOrderContainer {
  containerSeal?: string;
  containerCode?: string;
}

export interface ISolCustomerOrderAmounts {
  commodityCode?: string;
  commodityDescription?: string;
  currencyIsoCode?: string;
  currencyDescription?: string;
  totalAmount?: string;
  quantity?: string;
}

export interface ISolCustomerOrderDocument {
  documentNumber?: string;
  container?: ISolCustomerOrderContainer[];
  amounts?: ISolCustomerOrderAmounts[];
}
export interface ISolCustomerOrderService {
  servicePointId?: string;
  userName?: string;
  countryCode?: string;
  origin?: string;
  hasDate?: boolean;
  hasLimitDate?: boolean;
  comments?: string;
  description?: string;
  motiveId?: string;
  requestFinalTime?: string;
  requestInitialTime?: string;
  responsable?: string;
  serviceDate?: string;
  serviceId?: string;
  address?: string;
  applicant?: string;
  centerDescription?: string;
  container?: ISolCustomerOrderServiceContainer[];
  custodianName?: string;
  documents?: ISolCustomerOrderDocument[];
  entityDescription?: string;
  plannedFinalTime?: string;
  plannedInitialTime?: string;
  realFinalTime?: string;
  realInitialTime?: string;
  routeCode?: string;
  serviceCode?: string;
  serviceLimitDate?: string;
  servicePointDescription?: string;
  motiveCode?: string;
  motiveDescription?: string;
  observation?: string;
}

export interface ISolCustomerCancellationReason {
  code?: string;
  description?: string;
}

export interface ISolCommodityCounter {
  commodityId?: string;
  maxQuantity?: number;
  counter?: number;
}
